// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {useLayoutEffect, useRef, useState, type ComponentProps} from 'react'
import {gsap} from 'gsap'
import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'

import styles from './MonaFooterGrid.module.css'
import type {MonaFooterProps} from '../types'
import {getMonaFooterLayoutSnapshot} from '../use-mona-footer-layout'
import {GRID_LAYOUTS, type GridLayout} from './utils/grid-layout'

interface MotionOptions {
  animate: boolean
  showFinalState: boolean
  prefersReducedMotion: boolean
  layout: GridLayout
  repos: MonaFooterProps['repos'] | undefined
  onLayoutChange: (() => void) | undefined
}

type RepositoryInteractionProps = Pick<
  ComponentProps<'a'>,
  'onPointerEnter' | 'onPointerLeave' | 'onPointerCancel' | 'onFocus' | 'onBlur'
>

function createIntroAnimation(
  grid: HTMLElement,
  colorTiles: string[],
  hiddenPanels: HTMLElement[],
  nameAnimations: Map<HTMLElement, gsap.core.Timeline>,
  onComplete: () => void,
) {
  const tl = gsap.timeline({
    delay: 0.8,
  })
  tl.to(colorTiles, {
    '--opacity': 1,
    duration: 0.6,
    stagger: {each: 0.3, from: 'start'},
    ease: 'power2.out',
  })
  tl.to(colorTiles, {
    '--opacity': 0,
    duration: 0.6,
    stagger: {each: 0.1, from: 'start'},
    ease: 'power2.out',
  })
  if (hiddenPanels.length > 0) {
    const revealStart = tl.duration() - 0.8
    const revealStagger = 0.12

    tl.to(
      hiddenPanels,
      {
        autoAlpha: 1,
        duration: 0.6,
        stagger: revealStagger,
        ease: 'power2.out',
      },
      revealStart,
    )

    for (const [index, panel] of hiddenPanels.entries()) {
      const panelRevealStart = revealStart + index * revealStagger
      const animation = nameAnimations.get(panel)
      if (animation) tl.add(animation.play(0), panelRevealStart)

      const description = panel.querySelector<HTMLElement>(`.${styles.RepositoryDescription}`)
      if (description) {
        tl.fromTo(description, {opacity: 0}, {opacity: 1, duration: 0.3, ease: 'power2.out'}, panelRevealStart + 0.3)
      }
    }
  }
  tl.set(grid, {attr: {'data-repositories-ready': 'true'}})
  tl.call(onComplete)
}

function createRepositoryHoverAnimation(panel: HTMLElement, name: HTMLElement, grid: HTMLElement) {
  const hoverAnimation = getScrambleTextAnimation(name, 0.6, {revealDelay: 0, useMask: false}).pause()
  let isHovered = false
  let isFocused = false
  const handleInteraction = (event: Event) => {
    if (event instanceof PointerEvent && event.pointerType === 'touch') return

    const wasInteracting = isHovered || isFocused
    if (event.type === 'pointerenter') isHovered = true
    if (event.type === 'pointerleave') isHovered = false
    if (event.type === 'focusin') isFocused = true
    if (event.type === 'focusout') isFocused = false

    if ((isHovered || isFocused) && !wasInteracting && grid.getAttribute('data-repositories-ready') === 'true') {
      hoverAnimation.restart()
    }
  }

  panel.addEventListener('pointerenter', handleInteraction)
  panel.addEventListener('pointerleave', handleInteraction)
  panel.addEventListener('focusin', handleInteraction)
  panel.addEventListener('focusout', handleInteraction)
  return {
    animation: hoverAnimation,
    removeListeners() {
      panel.removeEventListener('pointerenter', handleInteraction)
      panel.removeEventListener('pointerleave', handleInteraction)
      panel.removeEventListener('focusin', handleInteraction)
      panel.removeEventListener('focusout', handleInteraction)
    },
  }
}

function createTileHoverAnimation(tile: HTMLElement, property: 'opacity' | '--opacity' = 'opacity') {
  const initialOpacity = tile.style.getPropertyValue(property)
  let tween: gsap.core.Tween | undefined
  let activeOpacity = 0
  let fadingIn = false
  const fadeOut = () => {
    tween = gsap.to(tile, {[property]: 0, duration: 0.3, delay: 0.5, ease: 'power2.out'})
  }

  return {
    setActiveOpacity(opacity: number) {
      if (opacity === activeOpacity) return
      const wasActive = activeOpacity > 0
      activeOpacity = opacity
      if (opacity > 0) {
        tween?.kill()
        fadingIn = true
        tween = gsap.to(tile, {
          [property]: opacity,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: () => {
            fadingIn = false
            if (activeOpacity === 0) fadeOut()
          },
        })
      } else if (wasActive && !fadingIn) {
        fadeOut()
      }
    },
    dispose() {
      tween?.kill()
      tile.style.setProperty(property, initialOpacity)
    },
  }
}

export function useMotion({
  animate,
  showFinalState,
  prefersReducedMotion,
  layout,
  repos,
  onLayoutChange,
}: MotionOptions) {
  const gridRef = useRef<HTMLDivElement>(null)
  const previousLayoutRef = useRef<GridLayout | null>(null)
  const revealedRepositoriesRef = useRef(new Set<string>())
  const repositoriesReadyRef = useRef(false)
  const neighborAnimationsRef = useRef(new Map<HTMLElement, ReturnType<typeof createTileHoverAnimation>>())
  const [repositoriesReady, setRepositoriesReady] = useState(false)
  const [hoveredRepositoryIndex, setHoveredRepositoryIndex] = useState<number | null>(null)
  const [focusedRepositoryIndex, setFocusedRepositoryIndex] = useState<number | null>(null)
  const activeRepositoryCells = layout.cells.filter(
    cell =>
      cell.repositoryIndex !== null &&
      (cell.repositoryIndex === hoveredRepositoryIndex || cell.repositoryIndex === focusedRepositoryIndex),
  )

  useLayoutEffect(() => {
    // Hydration initially uses the server layout, not the browser's actual layout.
    const clientLayout = GRID_LAYOUTS[getMonaFooterLayoutSnapshot()]
    const previousLayout = previousLayoutRef.current
    previousLayoutRef.current = clientLayout
    if (previousLayout !== null && previousLayout !== clientLayout) {
      onLayoutChange?.()
    }
  }, [layout, onLayoutChange])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const revealedRepositories = revealedRepositoriesRef.current
    const panels = layout.revealCells.flatMap(cell => {
      const panel = grid.querySelector<HTMLElement>(`[data-cell="${cell.id}"] .${styles.RepositoryPanel}`)
      return panel ? [panel] : []
    })
    const nameCleanups: Array<() => void> = []

    const context = gsap.context(() => {
      const nameAnimations = new Map<HTMLElement, gsap.core.Timeline>()

      if (!prefersReducedMotion && (animate || showFinalState || repositoriesReadyRef.current)) {
        for (const panel of panels) {
          const name = panel.querySelector<HTMLElement>(`.${styles.RepositoryNameText}`)
          const text = name?.firstElementChild
          if (!name || !text) continue

          const animation = getScrambleTextAnimation(name, 0.6, {revealDelay: 0, entranceDelay: 0}).pause()
          animation.progress(1)
          nameAnimations.set(panel, animation)
          const hover = createRepositoryHoverAnimation(panel, name, grid)

          nameCleanups.push(() => {
            hover.removeListeners()
            animation.kill()
            hover.animation.kill()
            // The following sibling holds React's current accessible name and is never scrambled.
            const staticName = name.nextElementSibling
            if (staticName) text.textContent = staticName.textContent
          })
        }
      }

      const isRevealed = (panel: HTMLElement) => {
        const index = panel.getAttribute('data-repository-index')
        return index !== null && revealedRepositories.has(index)
      }
      const revealedPanels = panels.filter(isRevealed)
      if (revealedPanels.length > 0) gsap.set(revealedPanels, {autoAlpha: 1, attr: {'data-revealed': 'true'}})
      const colorTiles = layout.colorTiles.map(({col, row}) => `[data-cell="cell-${col}-${row}"] .${styles.ColorTile}`)
      if (showFinalState || prefersReducedMotion || repositoriesReadyRef.current) {
        gsap.set(colorTiles, {'--opacity': 0})
        if (panels.length > 0) gsap.set(panels, {autoAlpha: 1})
        gsap.set(grid, {attr: {'data-repositories-ready': 'true'}})
        setRepositoriesReady(true)
        return
      }
      if (!animate) return

      createIntroAnimation(
        grid,
        colorTiles,
        panels.filter(panel => !isRevealed(panel)),
        nameAnimations,
        () => setRepositoriesReady(true),
      )
    }, grid)

    return () => {
      for (const panel of panels) {
        const index = panel.getAttribute('data-repository-index')
        if (index !== null && panel.style.visibility && panel.style.visibility !== 'hidden') {
          revealedRepositories.add(index)
        }
      }
      repositoriesReadyRef.current = grid.getAttribute('data-repositories-ready') === 'true'
      context.revert()
      for (const cleanup of nameCleanups) cleanup()
    }
  }, [animate, showFinalState, prefersReducedMotion, repos?.items, layout])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid || prefersReducedMotion) return

    const cleanups: Array<() => void> = []
    for (const cell of grid.querySelectorAll<HTMLElement>(`.${styles.Rect}`)) {
      const tile = cell.querySelector<HTMLElement>(`.${styles.HoverTile}`)
      if (!tile) continue

      const animation = createTileHoverAnimation(tile)
      const enter = (event: PointerEvent) => {
        if (
          event.pointerType === 'touch' ||
          !window.matchMedia('(hover: hover)').matches ||
          grid.getAttribute('data-repositories-ready') !== 'true'
        )
          return
        animation.setActiveOpacity(1)
      }
      const leave = () => {
        animation.setActiveOpacity(0)
      }
      cell.addEventListener('pointerenter', enter)
      cell.addEventListener('pointerleave', leave)
      cleanups.push(() => {
        cell.removeEventListener('pointerenter', enter)
        cell.removeEventListener('pointerleave', leave)
        animation.dispose()
      })
    }

    const neighborAnimations = neighborAnimationsRef.current
    for (const tile of grid.querySelectorAll<HTMLElement>(`.${styles.NeighborTile}`)) {
      neighborAnimations.set(tile, createTileHoverAnimation(tile, '--opacity'))
    }

    return () => {
      for (const cleanup of cleanups) cleanup()
      for (const animation of neighborAnimations.values()) animation.dispose()
      neighborAnimations.clear()
    }
  }, [layout, prefersReducedMotion, repos?.items])

  useLayoutEffect(() => {
    if (!repositoriesReady) return

    for (const [tile, animation] of neighborAnimationsRef.current) {
      animation.setActiveOpacity(
        tile.getAttribute('data-neighbor-focused') === 'true'
          ? Number(getComputedStyle(tile).getPropertyValue('--neighbor-target-opacity'))
          : 0,
      )
    }
  }, [repositoriesReady, hoveredRepositoryIndex, focusedRepositoryIndex, layout, prefersReducedMotion, repos?.items])

  const getRepositoryInteractionProps = (repositoryIndex: number | null): RepositoryInteractionProps => ({
    onPointerEnter: event => {
      if (event.pointerType !== 'touch' && window.matchMedia('(hover: hover)').matches) {
        setHoveredRepositoryIndex(repositoryIndex)
      }
    },
    onPointerLeave: () => setHoveredRepositoryIndex(null),
    onPointerCancel: () => setHoveredRepositoryIndex(null),
    onFocus: event => {
      if (event.currentTarget.matches(':focus-visible')) setFocusedRepositoryIndex(repositoryIndex)
    },
    onBlur: () => setFocusedRepositoryIndex(null),
  })

  return {gridRef, activeRepositoryCells, getRepositoryInteractionProps}
}
